import {
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import {
  ContextLostError,
  isCompositorSupported,
  kitchenMaterialBinding,
  SceneCompositor,
  type MaterialBinding,
} from '@cfg/render-2d';
import {
  pick,
  tierFor,
  type KitchenCatalog,
  type KitchenMaterial,
  type Lighting,
  type ZoneKey,
} from '@cfg/shared';
import { ExpandIcon, IconButton, ResetIcon, Spinner, ZoomInIcon, ZoomOutIcon } from '@cfg/ui';
import { useI18n } from '../i18n';

export interface ViewerHandle {
  exportImage(): Promise<Blob>;
}

interface ViewerProps {
  catalog: KitchenCatalog;
  sceneKey: string;
  lighting: Lighting;
  selections: Partial<Record<ZoneKey, KitchenMaterial>>;
  highlight: ZoneKey | null;
  onHoverZone: (zone: ZoneKey | null) => void;
  onPickZone: (zone: ZoneKey) => void;
  onError: (message: string) => void;
}

const TEXTURE_SIZE = typeof window !== 'undefined' && window.innerWidth < 900 ? 1024 : 2048;

function isContextLost(error: unknown): boolean {
  return (
    error instanceof ContextLostError ||
    (error instanceof Error && error.name === 'ContextLostError')
  );
}

export const Viewer = forwardRef<ViewerHandle, ViewerProps>(function Viewer(
  { catalog, sceneKey, lighting, selections, highlight, onHoverZone, onPickZone, onError },
  ref,
) {
  const { t, locale } = useI18n();
  const frameRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const compositorRef = useRef<SceneCompositor | null>(null);
  const [supported] = useState(() => isCompositorSupported());
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [zoomed, setZoomed] = useState(false);
  const loadedScene = useRef<string | null>(null);
  const scene = catalog.scenes.find((s) => s.key === sceneKey)!;
  const keysHintId = useId();

  // The compositor is created once; its event handler reads the latest callback and language.
  const onErrorRef = useRef(onError);
  const tRef = useRef(t);
  useEffect(() => {
    onErrorRef.current = onError;
    tRef.current = t;
  });
  /** Shows a render problem in the visitor's language (technical details go to the console). */
  const reportError = (error: unknown) => {
    if (isContextLost(error)) {
      onErrorRef.current(tRef.current('contextLost'));
      return;
    }
    console.warn('[viewer]', error instanceof Error ? error.message : error);
    onErrorRef.current(tRef.current('errorGeneric'));
  };

  const previewUrl = useMemo(() => {
    const tier = tierFor(
      scene.manifest,
      Math.min(1920, (typeof window !== 'undefined' ? window.innerWidth : 1280) * 1.5),
    );
    return `${scene.baseUrl}/${tier.dir}/${scene.manifest.preview}`;
  }, [scene]);

  useImperativeHandle(ref, () => ({
    exportImage: () => {
      if (!compositorRef.current) return Promise.reject(new Error('not ready'));
      return compositorRef.current.exportImage('image/jpeg', 0.92);
    },
  }));

  // create / dispose
  useEffect(() => {
    if (!supported || !canvasRef.current) return;
    const compositor = new SceneCompositor({
      canvas: canvasRef.current,
      background: '#ebe8e2',
      maxTierWidth: window.innerWidth < 700 ? 1280 : undefined,
      onEvent: (e) => {
        if (e.type === 'ready') setReady(true);
        if (e.type === 'error') reportError(e.error);
      },
    });
    compositorRef.current = compositor;
    (window as unknown as { __compositor?: SceneCompositor }).__compositor = compositor;
    return () => {
      compositor.dispose();
      compositorRef.current = null;
      loadedScene.current = null;
    };
  }, [supported]);

  const bindings = useMemo(() => {
    const out: Record<string, MaterialBinding | null> = {};
    for (const zone of scene.manifest.zones as ZoneKey[]) {
      const material = selections[zone];
      out[zone] = material ? kitchenMaterialBinding(material, zone, TEXTURE_SIZE) : null;
    }
    return out;
  }, [scene, selections]);

  // scene + materials
  useEffect(() => {
    const compositor = compositorRef.current;
    if (!compositor) return;
    let cancelled = false;
    const run = async () => {
      if (loadedScene.current !== scene.key) {
        setReady(false);
        setBusy(true);
        loadedScene.current = scene.key;
        compositor.resetView();
        setZoomed(false);
        await compositor.setScene({ baseUrl: scene.baseUrl, manifest: scene.manifest }, lighting);
      }
      if (cancelled) return;
      setBusy(true);
      await compositor.setMaterials(bindings);
      if (!cancelled) setBusy(false);
    };
    run().catch((error: unknown) => {
      if (!cancelled) {
        setBusy(false);
        reportError(error);
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene, bindings]);

  useEffect(() => {
    void compositorRef.current?.setLighting(lighting);
  }, [lighting]);

  useEffect(() => {
    compositorRef.current?.setHighlight(highlight);
  }, [highlight]);

  // pointer interaction: hover highlight, click to select, drag to pan, pinch/wheel to zoom
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const pointers = new Map<number, { x: number; y: number }>();
    let moved = false;
    let pinchDistance = 0;
    const compositor = () => compositorRef.current;

    const onMove = (e: PointerEvent) => {
      const c = compositor();
      if (!c) return;
      const previous = pointers.get(e.pointerId);
      if (previous) {
        const dx = e.clientX - previous.x;
        const dy = e.clientY - previous.y;
        if (Math.abs(dx) + Math.abs(dy) > 2) moved = true;
        pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pointers.size === 2) {
          const [a, b] = [...pointers.values()];
          const distance = Math.hypot(a!.x - b!.x, a!.y - b!.y);
          if (pinchDistance > 0) {
            c.zoomAt((a!.x + b!.x) / 2, (a!.y + b!.y) / 2, distance / pinchDistance);
            setZoomed(c.zoom > 1.01);
          }
          pinchDistance = distance;
        } else if (c.zoom > 1.01) {
          c.panBy(dx, dy);
        }
        return;
      }
      if (e.pointerType === 'mouse') {
        const zone = c.pick(e.clientX, e.clientY) as ZoneKey | null;
        canvas.style.cursor = zone ? 'pointer' : c.zoom > 1.01 ? 'grab' : 'default';
        onHoverZone(zone);
      }
    };
    const onDown = (e: PointerEvent) => {
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      moved = false;
      pinchDistance = 0;
      canvas.setPointerCapture(e.pointerId);
    };
    const onUp = (e: PointerEvent) => {
      pointers.delete(e.pointerId);
      pinchDistance = 0;
      const c = compositor();
      if (!moved && c && pointers.size === 0) {
        const zone = c.pick(e.clientX, e.clientY) as ZoneKey | null;
        if (zone) onPickZone(zone);
      }
    };
    const onLeave = () => onHoverZone(null);
    const onWheel = (e: WheelEvent) => {
      const c = compositor();
      if (!c) return;
      e.preventDefault();
      c.zoomAt(e.clientX, e.clientY, e.deltaY < 0 ? 1.12 : 1 / 1.12);
      setZoomed(c.zoom > 1.01);
    };
    const onDouble = () => {
      compositor()?.resetView();
      setZoomed(false);
    };
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointercancel', onUp);
    canvas.addEventListener('pointerleave', onLeave);
    canvas.addEventListener('wheel', onWheel, { passive: false });
    canvas.addEventListener('dblclick', onDouble);
    return () => {
      canvas.removeEventListener('pointermove', onMove);
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('pointercancel', onUp);
      canvas.removeEventListener('pointerleave', onLeave);
      canvas.removeEventListener('wheel', onWheel);
      canvas.removeEventListener('dblclick', onDouble);
    };
  }, [onHoverZone, onPickZone]);

  const zoomBy = (factor: number) => {
    const c = compositorRef.current;
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!c || !rect) return;
    c.zoomAt(rect.left + rect.width / 2, rect.top + rect.height / 2, factor);
    setZoomed(c.zoom > 1.01);
  };

  const resetView = () => {
    compositorRef.current?.resetView();
    setZoomed(false);
  };

  // keyboard alternative to dragging and pinching (WCAG 2.1.1 / 2.5.7)
  const onStageKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const c = compositorRef.current;
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!c || !rect || e.altKey || e.ctrlKey || e.metaKey) return;
    const step = (e.shiftKey ? 0.3 : 0.1) * Math.min(rect.width, rect.height);
    switch (e.key) {
      case '+':
      case '=':
        zoomBy(1.25);
        break;
      case '-':
      case '_':
        zoomBy(0.8);
        break;
      case '0':
        resetView();
        break;
      case 'ArrowLeft':
      case 'ArrowRight':
      case 'ArrowUp':
      case 'ArrowDown':
        // nothing to pan at full view: leave the keys to the page (scrolling)
        if (c.zoom <= 1.01) return;
        // the view moves in the arrow's direction, whatever the reading direction
        c.panBy(
          e.key === 'ArrowLeft' ? step : e.key === 'ArrowRight' ? -step : 0,
          e.key === 'ArrowUp' ? step : e.key === 'ArrowDown' ? -step : 0,
        );
        break;
      default:
        return;
    }
    e.preventDefault();
  };

  const description = catalog.zones
    .filter((z) => selections[z.key] && scene.manifest.zones.includes(z.key))
    .map((z) => `${pick(z.name, locale)} – ${pick(selections[z.key]!.name, locale)}`)
    .join('; ');
  const sceneAlt = t('sceneAlt', {
    desc: `${pick(scene.name, locale)}, ${lighting === 'day' ? t('day') : t('night')}. ${description}`,
  });

  return (
    <div className="viewer" ref={frameRef}>
      <div
        className="viewer__stage"
        role={supported ? 'group' : 'img'}
        tabIndex={supported ? 0 : undefined}
        aria-label={supported ? t('viewer') : sceneAlt}
        aria-describedby={supported ? keysHintId : undefined}
        onKeyDown={supported ? onStageKeyDown : undefined}
      >
        <img
          className={`viewer__preview ${ready ? 'is-hidden' : ''}`}
          src={previewUrl}
          alt=""
          draggable={false}
        />
        {supported ? (
          <canvas
            ref={canvasRef}
            className={`viewer__canvas ${ready ? '' : 'is-loading'}`}
            role="img"
            aria-label={sceneAlt}
          />
        ) : (
          <div className="viewer__fallback">{t('webglMissing')}</div>
        )}
        {(!ready || busy) && supported && (
          <div className="viewer__status" aria-live="polite">
            <Spinner label={!ready ? t('loadingScene') : undefined} size={18} />
          </div>
        )}
        {supported && (
          <span id={keysHintId} hidden>
            {t('viewerKeys')}
          </span>
        )}
      </div>
      {supported && (
        <div className="viewer__controls">
          <IconButton
            size="sm"
            variant="secondary"
            label={t('zoomIn')}
            icon={<ZoomInIcon size={18} />}
            onClick={() => zoomBy(1.25)}
          />
          <IconButton
            size="sm"
            variant="secondary"
            label={t('zoomOut')}
            icon={<ZoomOutIcon size={18} />}
            onClick={() => zoomBy(0.8)}
          />
          {zoomed && (
            <IconButton
              size="sm"
              variant="secondary"
              label={t('fitView')}
              icon={<ResetIcon size={18} />}
              onClick={resetView}
            />
          )}
          <IconButton
            size="sm"
            variant="secondary"
            label={t('fullscreen')}
            icon={<ExpandIcon size={18} />}
            onClick={() => {
              const el = frameRef.current;
              if (!el) return;
              if (document.fullscreenElement) void document.exitFullscreen();
              else void el.requestFullscreen?.();
            }}
          />
        </div>
      )}
    </div>
  );
});
